<template>
  <div>
    <v-simple-table dense>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">
              Unit code
            </th>
            <th class="text-left">
              Status
            </th>
            <th class="text-left">
              Assessment
            </th>
            <th class="text-end">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!assessments.length">
            <td colspan="5" class="text-center">
              {{ $apollo.queries.assessments.loading ? 'Loading' : 'No assessments' }}
            </td>
          </tr>
          <tr
            v-for="assessment of assessments"
            :key="assessment._id"
          >
            <td>{{ assessment.unitCode }}</td>
            <td>{{ assessment.passed ? 'Pass' : 'Fail' }}</td>
            <td>{{ assessment.assessment * 100 }}%</td>
            <td class="text-end">
              <v-btn
                icon
                small
                @click="editAssessment(assessment)"
              >
                <v-icon>
                  edit
                </v-icon>
              </v-btn>
              <v-btn
                icon
                small
                @click="deleteAssessment(assessment)"
                color="danger"
              >
                <v-icon>
                  delete
                </v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-form v-if="session.module" class="ma-3">
      <v-row>
        <v-col cols="12" lg="3">
          <v-select
            label="Unit"
            name="trainingModuleUnit"
            v-model="unitCode"
            :items="unitCodeItems"
            item-text="code"
            item-value="code"
          >
            <template v-slot:append>
              <v-btn
                color="primary"
                depressed
                tile
                @click.stop="addAssessment"
                :disabled="!unitCode"
              >
                Add
              </v-btn>
            </template>
          </v-select>
        </v-col>
      </v-row>
    </v-form>

    <v-btn
      color="primary"
      @click="downloadCertificate"
      :disabled="assessments.length < 1"
      :loading="downloadingCertificate"
    >
      <v-icon>picture_as_pdf</v-icon>
      Download Certificate
    </v-btn>

    <v-dialog
      v-model="assessmentDialog"
      width="800"
    >
      <v-card v-if="person">
        <v-card-title class="headline grey lighten-2">
          {{ person.name }} - {{ unitCode }}
        </v-card-title>

        <v-card-text>
          <v-form class="mt-5">
            <v-text-field
              label="Assessment"
              name="assessment"
              type="number"
              suffix="%"
              :value="assessmentForm.assessment * 100"
              @change="v => assessmentForm.assessment = v / 100"
            />
            <v-select
              label="Outcome"
              name="passed"
              v-model="assessmentForm.passed"
              :items="[
                { text: 'Passed', value: true },
                { text: 'Failed', value: false }
              ]"
            />
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="saveAssessment"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="assessmentDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import log from 'loglevel'
import gql from 'graphql-tag'
import { generateTrainingCertificatePdf } from '../integrations/mymsa-pdf'

export default {
  name: 'TraineeAssessmentList',

  props: {
    personId: {
      type: String,
      required: true
    },
    sessionId: {
      type: String,
      required: true
    }
  },

  data: () => ({
    assessments: [],
    session: {},
    unitCode: null,
    assessmentForm: {},
    assessmentDialog: false,
    downloadingCertificate: false
  }),

  computed: {
    unitCodeItems () {
      if (!this.session?.module?.units) return []
      return this.session.module.units.map(unit => {
        const disabled = this.assessments.some(o => o.unitCode === unit.code)
        return {
          disabled,
          code: unit.code
        }
      })
    }
  },

  apollo: {
    person: {
      query: gql`query GetPerson($personId: MongoID!) {
        person: personById(_id: $personId) {
          _id
          name
        }
      }`,
      variables () {
        return {
          personId: this.personId
        }
      },
      error (e) {
        this.onError(e)
      }
    },

    assessments: {
      query: gql`query TraineeAssessments ($filter: FilterFindManyTrainingAssessmentInput) {
        assessments: trainingAssessmentMany(limit: 0, filter: $filter) {
          _id
          unitCode
          passed
          assessment
          sessionId
          session {
            _id
            scheduledDate
            completionDate
            module {
              _id
              units {
                _id
                code
                description
              }
            }
          }
          personId
          person {
            _id
            name
          }
        }
      }`,
      variables () {
        return {
          filter: {
            personId: this.personId,
            sessionId: this.sessionId
          }
        }
      },
      error (e) { this.onError(e) }
    },

    session: {
      query: gql`query SessionModuleUnits ($sessionId: MongoID!) {
        session: trainingSessionById(_id: $sessionId) {
          _id
          module {
            _id
            units {
              _id
              code
            }
          }
        }
      }`,
      variables () {
        return {
          sessionId: this.sessionId
        }
      },
      error (e) { this.onError(e) }
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    },

    addAssessment () {
      this.assessmentForm = {
        assessment: null,
        passed: false
      }
      this.assessmentDialog = true
    },

    editAssessment (assessment) {
      this.unitCode = assessment.unitCode
      this.assessmentForm = {
        _id: assessment._id,
        assessment: assessment.assessment,
        passed: assessment.passed
      }
      this.assessmentDialog = true
    },

    async saveAssessment () {
      const id = this.assessmentForm._id
      const record = {
        personId: this.personId,
        sessionId: this.sessionId,
        unitCode: this.unitCode,
        assessment: this.assessmentForm.assessment,
        passed: this.assessmentForm.passed
      }
      let mutation
      if (id) {
        mutation = gql`mutation ($id: MongoID!, $record: UpdateByIdTrainingAssessmentInput!) {
          trainingAssessmentUpdateById(_id: $id, record: $record) { recordId }
        }`
      } else {
        mutation = gql`mutation ($record: CreateOneTrainingAssessmentInput!) {
          trainingAssessmentCreateOne(record: $record) { recordId }
        }`
      }
      try {
        await this.$apollo.mutate({
          mutation,
          variables: {
            id,
            record
          }
        })
        await this.$apollo.queries.assessments.refresh()
      } catch (e) {
        this.onError(e)
        return
      }
      this.unitCode = null
      this.assessmentDialog = false
    },

    async deleteAssessment (assessment) {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!) {
            trainingAssessmentRemoveById(_id: $id) { recordId }
          }`,
          variables: {
            id: assessment._id
          }
        })
        await this.$apollo.queries.assessments.refresh()
      } catch (e) {
        this.onError(e)
        return
      }
      this.unitCode = null
      this.assessmentDialog = false
    },

    async downloadCertificate () {
      this.downloadingCertificate = true
      let result
      try {
        result = await generateTrainingCertificatePdf(this.assessments)
      } catch (e) {
        log.error(e)
        this.onError(new Error('Failed to generate PDF. Please try again.'))
        return
      } finally {
        this.downloadingCertificate = false
      }
      window.open(result.presignedUrl, '_blank').focus()
      this.$emit('info', 'Certificate opened in new tab.')
    }
  }
}
</script>
